import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';

// Rotate a raster image by `deg` degrees and return a JPEG data URL of the
// resulting bitmap. The output canvas is expanded so no pixels are clipped.
async function rotateImageDataUrl(src, deg) {
  if (!src || !deg) return src;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const rad = (deg * Math.PI) / 180;
      const sin = Math.abs(Math.sin(rad));
      const cos = Math.abs(Math.cos(rad));
      const w = img.width;
      const h = img.height;
      const cw = Math.ceil(w * cos + h * sin);
      const ch = Math.ceil(w * sin + h * cos);
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cw, ch);
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -w / 2, -h / 2);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = reject;
    img.src = src;
  });
}

// Turn a pixel-space crop (from react-image-crop's completedCrop) into a JPEG blob.
export async function getCroppedBlob(imageEl, pixelCrop, mimeType = 'image/jpeg', quality = 0.92) {
  if (!imageEl || !pixelCrop || !pixelCrop.width || !pixelCrop.height) return null;
  const scaleX = imageEl.naturalWidth / imageEl.width;
  const scaleY = imageEl.naturalHeight / imageEl.height;
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(pixelCrop.width * scaleX);
  canvas.height = Math.floor(pixelCrop.height * scaleY);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    imageEl,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

// Modal that walks through a queue of File objects, letting the user free-form crop each one.
// Props:
//   files: File[]
//   open: bool
//   primaryColor: string
//   onFinish(croppedBlobs: {blob, name}[]): void
//   onCancel(): void
export default function ImageCropModal({ files, open, primaryColor = '#d97706', onFinish, onCancel }) {
  const [index, setIndex] = useState(0);
  const [originalSrc, setOriginalSrc] = useState(null); // as read from the file
  const [imageSrc, setImageSrc] = useState(null); // shown in ReactCrop (may be rotated)
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState(null); // display-space crop rect (px)
  const [completedCrop, setCompletedCrop] = useState(null); // committed rect
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  React.useEffect(() => {
    if (!open || !files || files.length === 0) return;
    setIndex(0);
    setResults([]);
    setCrop(null);
    setCompletedCrop(null);
    setRotation(0);
    loadFileToSrc(files[0]);
  }, [open, files]);

  // Whenever the rotation slider moves, re-render the loaded image onto a
  // canvas rotated by that angle and use it as the ReactCrop source. That
  // way the crop rectangle stays in sync with what the user actually sees.
  useEffect(() => {
    let cancelled = false;
    if (!originalSrc) return;
    if (!rotation) {
      setImageSrc(originalSrc);
      setCrop(null);
      setCompletedCrop(null);
      return;
    }
    rotateImageDataUrl(originalSrc, rotation).then((rotated) => {
      if (!cancelled) {
        setImageSrc(rotated);
        setCrop(null);
        setCompletedCrop(null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [rotation, originalSrc]);

  const loadFileToSrc = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setOriginalSrc(reader.result);
      setImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    // Start with a generous centered rectangle (90% of the image) so the user just drags the edges.
    const initial = centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, width / height, width, height),
      width,
      height,
    );
    // Remove aspect lock so drags are fully free-form.
    setCrop({ unit: initial.unit, x: initial.x, y: initial.y, width: initial.width, height: initial.height });
  }, []);

  const advanceOrFinish = (nextResults) => {
    if (index + 1 < files.length) {
      setResults(nextResults);
      setIndex(index + 1);
      setCrop(null);
      setCompletedCrop(null);
      setRotation(0);
      loadFileToSrc(files[index + 1]);
    } else {
      onFinish(nextResults);
    }
  };

  const handleUseAsIs = async () => {
    const file = files[index];
    // If the user leveled the image but chose not to crop, still bake the
    // rotation into the output so upload/save reflects what they saw.
    if (rotation && imageSrc) {
      try {
        setBusy(true);
        const blob = await new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const img = new Image();
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
          };
          img.onerror = () => resolve(null);
          img.src = imageSrc;
        });
        const name = (file.name || `image-${index + 1}.jpg`).replace(/\.\w+$/, '.jpg');
        advanceOrFinish([...results, { blob: blob || file, name }]);
      } finally {
        setBusy(false);
      }
      return;
    }
    advanceOrFinish([...results, { blob: file, name: file.name }]);
  };

  const handleCropAndNext = async () => {
    if (!completedCrop || !imgRef.current || !completedCrop.width || !completedCrop.height) {
      handleUseAsIs();
      return;
    }
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imgRef.current, completedCrop);
      const name = (files[index].name || `image-${index + 1}.jpg`).replace(/\.\w+$/, '.jpg');
      advanceOrFinish([...results, { blob: blob || files[index], name }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const total = files?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[95vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 pt-5 shrink-0">
          <DialogTitle className="text-xl">
            Crop image {index + 1} of {total}
          </DialogTitle>
          <p className="text-xs text-gray-500">Drag the corners or edges to any size. Drag inside to move the box. Or tap "Use as-is" to skip cropping.</p>
        </DialogHeader>

        <div className="relative bg-neutral-100 flex-1 min-h-0 flex items-center justify-center p-2 overflow-auto">
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              keepSelection
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{ maxHeight: '50vh', maxWidth: '100%', display: 'block' }}
                data-testid="crop-image"
              />
            </ReactCrop>
          )}
        </div>

        <div className="px-5 pt-3 pb-2 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <Label htmlFor="crop-rotation" className="text-sm font-medium">
              Leveling (rotate image)
            </Label>
            <span className="text-sm font-mono text-gray-700">
              {rotation.toFixed(1)}°
            </span>
          </div>
          <input
            id="crop-rotation"
            type="range"
            min="-15"
            max="15"
            step="0.1"
            value={rotation}
            onChange={(e) => setRotation(parseFloat(e.target.value))}
            className="w-full"
            data-testid="crop-rotation"
          />
          <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
            <span>-15°</span>
            <button
              type="button"
              onClick={() => setRotation(0)}
              className="underline"
              data-testid="crop-rotation-reset"
            >
              Reset
            </button>
            <span>+15°</span>
          </div>
        </div>

        <DialogFooter className="p-4 sm:p-5 flex-col sm:flex-row gap-2 shrink-0 border-t bg-white sticky bottom-0">
          <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto" data-testid="crop-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleUseAsIs}
            disabled={busy}
            className="w-full sm:w-auto"
            data-testid="crop-use-as-is"
          >
            Use as-is
          </Button>
          <Button
            type="button"
            onClick={handleCropAndNext}
            disabled={busy}
            className="w-full sm:w-auto text-white"
            style={{ backgroundColor: primaryColor }}
            data-testid="crop-continue"
          >
            {busy ? 'Cropping…' : (index + 1 < total ? 'Crop & Next' : 'Crop & Done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
